import { isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';

import i18n from '@/core/i18n';
import { RateSource } from '@/modules/common/types/rate-source';
import { getTodayISOString, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { getRateSourceLabel } from '../../../../helpers/rate-option';
import { RateOption } from '../../../../types';

/**
 * Computes the effective date to use for the rates API query.
 *
 * Rules (relative to today):
 * - If the assignment ends in the past → use its end date
 * - If the assignment starts in the future → use its start date
 * - Otherwise → use today
 */
export function getEffectiveDateForRatesQuery(
  committedDateFrom: Date | null | undefined,
  committedDateTo: Date | null | undefined
): string {
  const today = startOfDay(new Date());

  if (committedDateTo && isBefore(startOfDay(committedDateTo), today)) {
    return normalizeToStartOfDayUTC(committedDateTo).toISOString();
  }

  if (committedDateFrom && isAfter(startOfDay(committedDateFrom), today)) {
    return normalizeToStartOfDayUTC(committedDateFrom).toISOString();
  }

  return getTodayISOString();
}

interface RateParamsChangedArgs {
  isEditMode: boolean;
  hasInitialRateFromPeriods: boolean;
  projectId: string;
  initialProjectId: string | undefined;
  committedDateFrom: Date | null | undefined;
  committedDateTo: Date | null | undefined;
  initialDateFrom: Date | undefined;
  initialDateTo: Date | undefined;
}

/**
 * Returns true when the user has changed project or dates in edit mode,
 * which means the rates query should re-fire to get fresh data.
 *
 * Always returns true in create mode or when there is no initial rate from periods.
 */
export function hasRateParamsChanged({
  isEditMode,
  hasInitialRateFromPeriods,
  projectId,
  initialProjectId,
  committedDateFrom,
  committedDateTo,
  initialDateFrom,
  initialDateTo,
}: RateParamsChangedArgs): boolean {
  if (!isEditMode) return true;
  if (!hasInitialRateFromPeriods) return true;

  const projectChanged = projectId !== (initialProjectId ?? '');
  const dateFromChanged =
    committedDateFrom && initialDateFrom && !isSameDay(committedDateFrom, initialDateFrom);
  const dateToChanged =
    committedDateTo && initialDateTo && !isSameDay(committedDateTo, initialDateTo);

  return projectChanged || !!dateFromChanged || !!dateToChanged;
}

interface BuildInitialRateOptionsArgs {
  rateSource: RateSource;
  rate: string;
  currency: string | undefined;
  projectBoundRateSource?: RateSource | null;
  assignmentOverride: number | undefined;
}

/**
 * Builds minimal rate options for edit mode when the rates API hasn't responded yet
 * but we already have rate info from the assignment's active periods.
 *
 * This prevents the rate field from being empty while the API is loading.
 */
export function buildInitialRateOptionsFromPeriods({
  rateSource,
  rate,
  currency,
  projectBoundRateSource,
  assignmentOverride,
}: BuildInitialRateOptionsArgs): RateOption[] {
  const options: RateOption[] = [];
  const currencyCode = currency || '';

  if (rateSource !== RateSource.Custom) {
    let label: string;

    if (rateSource === RateSource.ProjectBased && projectBoundRateSource) {
      const innerLabel = getRateSourceLabel(projectBoundRateSource);
      label = i18n.t('common:rates.projectBased', { rateSourceName: innerLabel });
    } else {
      label = getRateSourceLabel(rateSource);
    }

    options.push({
      value: rateSource,
      label,
      disabled: false,
      rate: parseFloat(rate),
      currency: currencyCode,
    });
  }

  options.push({
    value: RateSource.Custom,
    label: i18n.t('common:rates.custom'),
    disabled: false,
    rate: assignmentOverride,
    currency: currencyCode,
  });

  return options;
}

export enum EffectiveDateContext {
  AsOfEndDate = 'asOfEndDate',
  AsOfStartDate = 'asOfStartDate',
  AsOfToday = 'asOfToday',
}

/**
 * Determines which effective-date context label to show next to the rate field.
 *
 * Returns null when no rate source is selected yet.
 */
export function getEffectiveDateContext(
  rateSource: RateSource | undefined,
  committedDateFrom: Date | null | undefined,
  committedDateTo: Date | null | undefined
): EffectiveDateContext | null {
  if (!rateSource) return null;

  const today = startOfDay(new Date());

  if (committedDateTo && isBefore(startOfDay(committedDateTo), today)) {
    return EffectiveDateContext.AsOfEndDate;
  }

  if (committedDateFrom && isAfter(startOfDay(committedDateFrom), today)) {
    return EffectiveDateContext.AsOfStartDate;
  }

  return EffectiveDateContext.AsOfToday;
}
